/**
 * PoseUnit — combined pose + linked action renderer.
 *
 * Three rendering states per spec §1:
 *   1. POSE with linked actions — header + action chips + prose body + reactions.
 *   2. POSE without linked actions — narrative-only card (existing SceneMessages format).
 *   3. ACTION standalone (not yet linked to any pose) — chip-only card.
 *
 * Phase 9, Task 9.2.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useViewerPersonaId } from '@/roster/persona';
import { excerptOf } from '@/lib/formatParser';
import { PersonaAvatar } from '@/components/PersonaAvatar';
import { FormattedContent } from '@/components/FormattedContent';
import { Badge } from '@/components/ui/badge';
import { NominateButton } from '@/components/NominateButton';
import { PersonaMenu, type PersonaMenuProps } from './PersonaMenu';
import { ActionResult } from './ActionResult';
import { ActorLine } from './ActorLine';
import { NarratedEventTag } from './NarratedEventTag';
import { ReactionStrip } from './ReactionStrip';
import { DramaticMomentTagDialog } from './DramaticMomentTagDialog';
import { EndorsementControl } from './EndorsementControl';
import { fetchReactionEmojiCatalog, postInteractionReaction } from '../queries';
import type { Interaction, ActionLink } from '../types';
import type { ActionAttachmentInfo } from '../actionTypes';
import { PoseUnitDetailPanel } from './PoseUnitDetailPanel';
import { useKudos } from '../hooks/useKudos';
import { sceneFeedSceneKey } from '@/scenes/sceneFeedCache';

// ---------------------------------------------------------------------------
// Action chip
// ---------------------------------------------------------------------------

interface ActionChipProps {
  link: ActionLink;
  onExpandRequest: () => void;
}

function ActionChip({ link, onExpandRequest }: ActionChipProps) {
  const { action_interaction } = link;
  return (
    <button
      type="button"
      className={cn(
        'flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5',
        'text-xs text-foreground transition-colors hover:bg-muted'
      )}
      onClick={onExpandRequest}
      title="Click to expand action details"
    >
      <span className="max-w-[16rem] truncate">{action_interaction.content}</span>
      <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Avatar identity affordance (#2156)
// ---------------------------------------------------------------------------

interface PoseUnitAvatarProps {
  interaction: Interaction;
  onAvatarClick?: (persona: PoseUnitAvatarClickPersona) => void;
}

/**
 * Avatar thumbnail on an action card's header. Identity click surface (#2156):
 * a left click opens the character card; since #4030 the avatar is also
 * wrapped in `PersonaMenu` so a right-click reaches the same action menu the
 * name offers. Renders as a plain (non-interactive) avatar when
 * `onAvatarClick` isn't provided. A pose line (#4128) does not use it: its
 * avatar's left click is the play menu, whose View sheet opens the card.
 */
function PoseUnitAvatar({ interaction, onAvatarClick }: PoseUnitAvatarProps) {
  // #3294 — no companion art exists; the owner's own thumbnail stands in even
  // when the companion is the displayed actor (see PoseUnitActorLabel).
  const avatar = (
    <PersonaAvatar
      source={{
        name: interaction.persona.name,
        thumbnailUrl: interaction.persona.thumbnail_url,
      }}
      size="sm"
    />
  );

  if (!onAvatarClick) {
    return avatar;
  }

  const button = (
    <button
      type="button"
      aria-label={`View ${interaction.persona.name}`}
      className="rounded-full transition-opacity hover:opacity-80"
      onClick={() =>
        onAvatarClick({
          id: interaction.persona.id,
          name: interaction.persona.name,
          thumbnail_url: interaction.persona.thumbnail_url ?? null,
        })
      }
    >
      {avatar}
    </button>
  );

  return (
    <PersonaMenu
      personaId={interaction.persona.id}
      personaName={interaction.persona.name}
      thumbnailUrl={interaction.persona.thumbnail_url}
    >
      {button}
    </PersonaMenu>
  );
}

// ---------------------------------------------------------------------------
// Companion attribution (#3294)
// ---------------------------------------------------------------------------

/**
 * A companion pose always shows an owner tell (#3294): honest puppetry, never a
 * hidden actor. The name itself is in the line (#3858); this is the tell alone,
 * after the body, since the header row that carried both is gone (#4128).
 */
function CompanionTell({ interaction }: { interaction: Interaction }) {
  if (!interaction.attributed_companion) return null;
  return (
    <span
      className="text-xs text-muted-foreground"
      title={`Puppeted by ${interaction.persona.name}`}
      data-testid="companion-owner-tell"
    >
      (via {interaction.persona.name})
    </span>
  );
}

// ---------------------------------------------------------------------------
// Reactions footer (mirrors SceneMessages pattern)
// ---------------------------------------------------------------------------

interface ReactionsFooterProps {
  interaction: Interaction;
  sceneId: string;
}

function ReactionsFooter({ interaction, sceneId }: ReactionsFooterProps) {
  const queryClient = useQueryClient();
  const reactionMutation = useMutation({
    mutationFn: (emoji: string) => postInteractionReaction(interaction.id, emoji),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: sceneFeedSceneKey(sceneId) });
      if (data?.bump_message) {
        toast.success(data.bump_message);
      }
    },
  });
  // Staff-editable catalog (#1699); valenced entries also nudge the author's regard.
  const { data: catalog } = useQuery({
    queryKey: ['reaction-emoji'],
    queryFn: fetchReactionEmojiCatalog,
    staleTime: 5 * 60 * 1000,
  });
  const existing = new Set(interaction.reactions.map((r) => r.emoji));
  const pickerEntries = (catalog ?? []).filter((entry) => !existing.has(entry.emoji));

  return (
    <div className="mt-1 flex gap-2">
      {interaction.reactions.map((r) => (
        <button key={r.emoji} className="text-sm" onClick={() => reactionMutation.mutate(r.emoji)}>
          {r.emoji} {r.count}
        </button>
      ))}
      {pickerEntries.map((entry) => (
        <button
          key={entry.emoji}
          className="text-sm opacity-60 transition-opacity hover:opacity-100"
          title={valenceTitle(entry.valence)}
          onClick={() => reactionMutation.mutate(entry.emoji)}
        >
          {entry.emoji}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parent-reply chip (#3787 demo Screen 2)
// ---------------------------------------------------------------------------

/**
 * "Answering "<excerpt>"" -- the parent chip. Replaces the placeholder
 * `Replying to pose {id}` now that `interaction.reply_to` carries real data
 * (#3787 Tasks 1-2). Clicking it reveals the parent's content in place,
 * matching the demo's "reveal-in-place" affordance.
 *
 * The chip NEVER re-derives an actor: both the quoted excerpt and the
 * revealed block below show only `parent.content` (already the exact,
 * per-viewer-rendered text the reader elsewhere shows for that row), never
 * `parent.persona.name`. That is what keeps it safe on a concealed working --
 * the line stays exactly as unattributed as it already was.
 */
function ParentChip({
  replyTo,
  parent,
}: {
  replyTo: { id: string; timestamp: string };
  parent: Interaction | undefined;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-1" data-testid="parent-reference">
      <button
        type="button"
        className="block w-full rounded-r border-l-2 border-primary/50 px-2 py-1 text-left text-xs text-muted-foreground transition-colors hover:text-primary"
        onClick={() => setRevealed((v) => !v)}
        aria-expanded={revealed}
        data-testid={`parent-chip-${replyTo.id}`}
      >
        Answering{' '}
        {parent ? (
          <span className="italic text-foreground">&ldquo;{excerptOf(parent.content)}&rdquo;</span>
        ) : (
          <span className="italic">a pose not currently loaded</span>
        )}
      </button>
      {revealed && parent && (
        <div
          className="ml-2 mt-1 rounded border border-dashed px-2 py-1 text-xs"
          data-testid={`parent-reveal-${replyTo.id}`}
        >
          <FormattedContent content={parent.content} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PoseUnit
// ---------------------------------------------------------------------------

/** Minimal persona identity payload forwarded by the avatar-click affordance (#2156). */
export interface PoseUnitAvatarClickPersona {
  id: number;
  name: string;
  thumbnail_url: string | null;
}

export interface PoseUnitProps {
  interaction: Interaction;
  sceneId: string;
  onAddTarget?: (personaName: string) => void;
  onAttachAction?: (action: ActionAttachmentInfo) => void;
  /** When true, shows the "Tag dramatic moment" GM control (#1139). */
  canGm?: boolean;
  /**
   * Avatar-click identity affordance (#2156): fired with the interaction's
   * persona when the avatar thumbnail is clicked. The avatar renders as a
   * plain (non-interactive) image when this prop is absent — the name's
   * PersonaMenu remains the action surface either way.
   */
  onAvatarClick?: (persona: PoseUnitAvatarClickPersona) => void;
  /** Historical readers must not mount mutation controls. */
  readOnly?: boolean;
  /** Reply to this pose (#4128): the first item of the avatar's play menu. */
  onReply?: (interaction: Interaction) => void;
  /**
   * Lookup for resolving `interaction.reply_to` to its parent Interaction
   * (#3787) -- `reply_to` itself carries only `{id, timestamp}` (a thread
   * selector, not the parent's content), so the parent chip's quoted
   * excerpt needs the full row. Built once by the caller that already holds
   * every loaded interaction (`ThreadedNarrativeReader.tsx`) rather than
   * fetched per-pose. Absent/a miss (the parent isn't in the currently
   * loaded window) degrades to a chip with no quote, never a fetch.
   */
  interactionsById?: ReadonlyMap<number, Interaction>;
}

/** Why a reaction chip nudges your regard, shown on hover. */
function valenceTitle(valence: number): string | undefined {
  if (valence > 0) return 'Warms your regard for the author';
  if (valence < 0) return 'Cools your regard for the author';
  return undefined;
}

function NarrationUnit({ interaction }: { interaction: Interaction }) {
  if (interaction.narrates) {
    const isPrivate = interaction.receiver_persona_ids.length > 0;
    if (isPrivate) {
      return (
        <div
          role="note"
          data-testid="pose-unit-narration-private"
          className="my-1.5 max-w-[85%] rounded-r-md border-l-2 border-primary bg-primary/10 px-3 py-2"
        >
          <p className="text-sm italic text-foreground">
            &ldquo;
            <FormattedContent content={interaction.content} />
            &rdquo;
          </p>
          <NarratedEventTag
            narrates={interaction.narrates}
            receiverPersonaIds={interaction.receiver_persona_ids}
          />
        </div>
      );
    }
    return (
      <div
        role="note"
        data-testid="pose-unit-narration"
        className="my-1.5 max-w-[85%] border-l-2 border-border pl-2"
      >
        <p className="text-sm italic text-muted-foreground">
          <FormattedContent content={interaction.content} />
        </p>
        <NarratedEventTag
          narrates={interaction.narrates}
          receiverPersonaIds={interaction.receiver_persona_ids}
        />
      </div>
    );
  }

  return null;
}

export function PoseUnit({
  interaction,
  sceneId,
  onAddTarget,
  onAttachAction,
  canGm = false,
  onAvatarClick,
  readOnly = false,
  onReply,
  interactionsById,
}: PoseUnitProps) {
  const isAction = interaction.mode === 'action';
  const actionLinks = interaction.action_links ?? [];
  const hasLinks = actionLinks.length > 0;

  // Auto-expand on first paint when a linked action had a critical outcome
  // (e.g. it defeated its focused opponent) so players don't miss it (#996).
  const [expanded, setExpanded] = useState(() => actionLinks.some((l) => l.has_critical_effect));
  const [tagDialogOpen, setTagDialogOpen] = useState(false);

  const actionInteractionIds = actionLinks.map((l) => l.action_interaction.id);
  const dramaticTags = interaction.dramatic_moment_tags ?? [];

  // Resolve the viewer's active persona to detect self-pose — mirrors
  // EndorsementControl's self-endorsement guard (same signal, same source).
  // NominateButton has no self-guard of its own (the backend refuses your own
  // characters; this gate is UX only), so PoseUnit computes it and decides
  // whether to mount (#3738). #3787: this is now `useViewerPersonaId()`, the
  // single extracted source of truth also used by `ThreadedNarrativeReader`'s
  // involvement mark -- do not add a second inline computation here.
  const viewerPersonaId = useViewerPersonaId();
  const isSelfPose = viewerPersonaId != null && interaction.persona.id === viewerPersonaId;
  const canNominate = Boolean(sceneId) && !isSelfPose;
  // The play menu's Kudos (#4128); the strip's standalone chip moved here.
  const kudos = useKudos(sceneId, interaction.id);

  // -------------------------------------------------------------------------
  // State 3: standalone ACTION (not linked to any pose)
  // -------------------------------------------------------------------------
  if (isAction) {
    return (
      <div
        className="my-1.5 max-w-[85%] rounded-lg bg-muted/40 px-3 py-2"
        data-testid="pose-unit-action-standalone"
      >
        <div className="flex items-center gap-2">
          <PoseUnitAvatar interaction={interaction} onAvatarClick={onAvatarClick} />
          <PersonaMenu
            personaId={interaction.persona.id}
            personaName={interaction.persona.name}
            leftClick
            onAttachAction={onAttachAction}
          >
            <span
              onDoubleClick={() => onAddTarget?.(interaction.persona.name)}
              className="cursor-pointer text-sm font-medium"
              title="Double-click to add as target"
            >
              {interaction.persona.name}
            </span>
          </PersonaMenu>
          <span className="text-xs text-muted-foreground">
            {new Date(interaction.timestamp).toLocaleString()}
          </span>
        </div>
        <div className="mt-1">
          <ActionResult content={interaction.content} />
        </div>
        <button
          type="button"
          className="mt-1 flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          onClick={() => setExpanded((v) => !v)}
          data-testid="standalone-action-expand"
          title="Click to expand action details"
        >
          <ChevronDown
            className={cn('h-3 w-3 shrink-0 transition-transform', expanded && 'rotate-180')}
          />
          details
        </button>
        {expanded && <PoseUnitDetailPanel actionInteractionIds={[interaction.id]} />}
        <div className="flex items-center gap-1">
          {!readOnly && <ReactionsFooter interaction={interaction} sceneId={sceneId} />}
          {!readOnly && canNominate && (
            <NominateButton
              targetType="interaction"
              targetId={interaction.id}
              nomineeName={interaction.persona.name}
            />
          )}
        </div>
        {/* Standalone ACTION rows are authored content (claimed resonances) and
            are endorsable per spec — this is intentional, not a slip. */}
        {!readOnly && (
          <EndorsementControl interaction={interaction} sceneId={sceneId} kind="pose" />
        )}
        {interaction.pose_kind === 'entry' && !readOnly && (
          <EndorsementControl interaction={interaction} sceneId={sceneId} kind="entry" />
        )}
        {!readOnly && (
          <EndorsementControl interaction={interaction} sceneId={sceneId} kind="style" />
        )}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // OUTCOME: combat result narration — authored by the Narrator, not a
  // targetable character, so no avatar / context menu / target affordance.
  // -------------------------------------------------------------------------
  if (interaction.mode === 'outcome') {
    return (
      <div
        className="my-1 pl-2 text-sm italic text-muted-foreground"
        data-testid="pose-unit-outcome"
      >
        <FormattedContent content={interaction.content} />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // NARRATION: a GM's EMIT/PEMIT line tied to a GM prompt (#4101, demo Screen
  // 3; F1). The GM already delivered this through EmitAction/PemitAction
  // (ADR-0293), so it is a narration, not a character's pose — no speaker
  // header, avatar, Kudos or nominate. The room leg (no receivers; mode
  // 'emit') reads as a speakerless line, mirroring the OUTCOME branch above.
  // The receiver-scoped leg (mode 'whisper', the private vision) renders as
  // its own tinted block. Theme tokens only — never the demo's literal amber.
  // -------------------------------------------------------------------------
  if (interaction.narrates) return <NarrationUnit interaction={interaction} />;

  // -------------------------------------------------------------------------
  // State 1 + 2: POSE (with or without linked actions): one prose line (#4128).
  // The actor is in the sentence (#3858), the avatar is an indent (a float: the
  // first line starts beside it, the rest wrap back under it), the time shows
  // on hover, and nothing else sits on the line. Left-click on the avatar is
  // the play menu (Reply, Kudos, then the persona's actions); the right button
  // is the sorting menu, owned by FeedBlockFrame, so the persona menu's own
  // right-click is off here.
  // -------------------------------------------------------------------------
  const avatar = (
    <PersonaAvatar
      source={{ name: interaction.persona.name, thumbnailUrl: interaction.persona.thumbnail_url }}
      size="xs"
    />
  );
  const posedAt = new Date(interaction.timestamp);
  // The play menu (#4128): on the avatar and, since #4791, on the name that
  // opens the line, so both carry the same Reply, Kudos and persona items. A
  // companion pose's name is the companion's, and its menu is the owner's, as
  // the avatar's is (#3294: a companion has no face or menu of its own).
  const playMenu: Omit<PersonaMenuProps, 'children'> = {
    personaId: interaction.persona.id,
    personaName: interaction.persona.name,
    thumbnailUrl: interaction.persona.thumbnail_url,
    leftClick: true,
    contextMenu: false,
    poseActions: {
      onReply: onReply ? () => onReply(interaction) : undefined,
      onKudos: kudos.give,
      kudosDisabled: !kudos.canGive,
    },
    onAttachAction,
  };
  const addTarget = onAddTarget ? () => onAddTarget(interaction.persona.name) : undefined;
  return (
    <div
      className="group relative -mx-2 rounded-md py-1 pl-2 pr-20 [display:flow-root] hover:bg-muted/40"
      data-testid="pose-unit"
      data-persona-id={interaction.persona.id}
      data-persona-name={interaction.persona.name}
    >
      <span
        className="float-left mr-2 flex"
        data-testid="pose-avatar"
        data-pose-avatar
        onDoubleClick={addTarget}
      >
        {readOnly ? avatar : <PersonaMenu {...playMenu}>{avatar}</PersonaMenu>}
      </span>
      <time
        dateTime={interaction.timestamp}
        title={posedAt.toLocaleString()}
        className="absolute right-2 top-1 text-xs text-muted-foreground opacity-0 group-focus-within:opacity-100 group-hover:opacity-100"
        data-testid="pose-time"
      >
        {posedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
      </time>

      {/* Action chips (state 1 only) */}
      {hasLinks && (
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="action-chips">
          {actionLinks.map((link) => (
            <ActionChip key={link.id} link={link} onExpandRequest={() => setExpanded((v) => !v)} />
          ))}
        </div>
      )}

      {interaction.reply_to && (
        <ParentChip
          replyTo={interaction.reply_to}
          parent={interactionsById?.get(Number(interaction.reply_to.id))}
        />
      )}

      {/* Prose body: the actor in the line (#3858); paragraph breaks kept (#4128) */}
      <div className="whitespace-pre-line" data-testid="pose-body">
        <p>
          <ActorLine
            line={interaction.line}
            content={interaction.content}
            actorName={interaction.attributed_companion?.name ?? interaction.persona.name}
            renderName={
              readOnly
                ? undefined
                : (name) => (
                    <span data-testid="pose-actor" data-pose-actor onDoubleClick={addTarget}>
                      <PersonaMenu {...playMenu}>{name}</PersonaMenu>
                    </span>
                  )
            }
          />
        </p>
      </div>

      <CompanionTell interaction={interaction} />

      {/* Expandable outcome detail panel */}
      {expanded && actionInteractionIds.length > 0 && (
        <PoseUnitDetailPanel actionInteractionIds={actionInteractionIds} />
      )}

      {!readOnly && (
        <ReactionStrip windows={interaction.reaction_windows ?? []} sceneId={sceneId} />
      )}

      {/* Dramatic-moment tag badges (#1139) */}
      {dramaticTags.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1" data-testid="dramatic-moment-badges">
          {dramaticTags.map((tag) => (
            <Badge
              key={`${tag.moment_type_label}-${tag.character_sheet_id ?? 'none'}`}
              variant="secondary"
              className="text-xs"
            >
              ✶ {tag.moment_type_label}
            </Badge>
          ))}
        </div>
      )}

      {/* GM control: tag a dramatic moment (#1139) */}
      {canGm && !readOnly && (
        <div className="mt-1">
          <button
            type="button"
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setTagDialogOpen(true)}
            data-testid="tag-moment-button"
          >
            ✶ Tag moment
          </button>
          <DramaticMomentTagDialog
            open={tagDialogOpen}
            onClose={() => setTagDialogOpen(false)}
            interactionId={interaction.id}
            sceneId={sceneId}
          />
        </div>
      )}

      <div className="flex items-center gap-1">
        {!readOnly && <ReactionsFooter interaction={interaction} sceneId={sceneId} />}
        {!readOnly && canNominate && (
          <NominateButton
            targetType="interaction"
            targetId={interaction.id}
            nomineeName={interaction.persona.name}
          />
        )}
      </div>
      {!readOnly && <EndorsementControl interaction={interaction} sceneId={sceneId} kind="pose" />}
      {interaction.pose_kind === 'entry' && !readOnly && (
        <EndorsementControl interaction={interaction} sceneId={sceneId} kind="entry" />
      )}
      {!readOnly && <EndorsementControl interaction={interaction} sceneId={sceneId} kind="style" />}
    </div>
  );
}
